import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { RgbaColor } from "@uiw/react-color";
import classNames from "classnames";
import { useSelector } from "react-redux";
import PiAssetSelect from "../../components/PiAssetSelect";
import PiColorPickerField from "../../components/PiColorPickerField";
import PiInputfield from "../../components/PiInputField";
import PiSegmentedToggle from "../../components/PiSegmentedToggle";
import PiSwitch from "../../components/PiSwitch";
import { selectCurrentLanguage } from "../../app/slices/TrainingLocalizationSlice";
import {
  ITransition,
  ITypewriterTransitionSettings,
  TransitionType,
} from "../../models/interactions/Transition";
import {
  normalizeTransition,
  withTransitionType,
} from "../../models/interactions/TransitionDefaults";
import ActorPresetEditorField from "../actor_presets/ActorPresetEditorField";
import RichTextEditor from "../rich_text/RichTextEditor";
import { useTrainingLocalization } from "../trainingLocalization/TrainingLocalizationHooks";
import "./TransitionEditor.css";

function TransitionRichTextField(props: {
  ariaLabel: string;
  label: string;
  placeholder: string;
  /** Localized key; "" means not allocated yet (typewriter keys before first use). */
  locKey: string;
  multiline?: boolean;
  className?: string;
}) {
  const { getValue, changeValue } = useTrainingLocalization();
  const currentLanguage = useSelector(selectCurrentLanguage);
  const disabled = props.locKey === "";

  return (
    <div
      className={classNames(
        "viewport3d-transition-richtext",
        props.className,
        disabled && "viewport3d-transition-richtext--disabled"
      )}
    >
      <div className="pi-inputfield-label">{props.label}</div>
      <RichTextEditor
        ariaLabel={props.ariaLabel}
        value={disabled ? "" : getValue(props.locKey)}
        onCommit={(nextValue) => {
          if (!disabled) {
            changeValue(props.locKey, nextValue, currentLanguage.shortcode);
          }
        }}
        variables={[]}
        allowVariables={false}
        checkVariables={false}
        enableVariableIntellisense={false}
        placeholder={props.placeholder}
        debounceMs={250}
        flushOnCleanupKeys={[currentLanguage.shortcode, props.locKey]}
        disabled={disabled}
        // Single-line fields block Enter newlines through the submit hook.
        onSubmitEnter={props.multiline ? undefined : () => false}
        className={
          props.multiline ? "relative flex min-h-0 flex-1 flex-col" : "relative"
        }
        contentClassName={classNames(
          "viewport3d-transition-richtext__content",
          props.multiline
            ? "viewport3d-transition-richtext__content--area"
            : "viewport3d-transition-richtext__content--line"
        )}
        warningClassName="mt-[4px] text-[12px] font-medium text-creator-error"
      />
    </div>
  );
}

interface TransitionEditorProps {
  transition: ITransition;
  onChange: (next: ITransition) => void;
  disabled?: boolean;
}

/**
 * The transition editor UI (type toggle + default/typewriter tile grids),
 * shared by the take-level ViewportTransitionWindow and the interaction-based
 * TransitionPreview. Text fields write to localization directly; everything
 * else funnels through onChange with the updated ITransition.
 */
export default function TransitionEditor({
  transition: rawTransition,
  onChange,
  disabled = false,
}: TransitionEditorProps) {
  const { GetArrayOfKeys } = useTrainingLocalization();
  const transition = normalizeTransition(rawTransition);

  const updateTransition = (newTransition: ITransition) => {
    onChange(newTransition);
  };

  const onChangeTransitionDuration = (duration: string) => {
    const parsedDuration = Number(duration);
    if (!Number.isFinite(parsedDuration)) return;

    updateTransition({
      ...transition,
      duration: parsedDuration,
    });
  };

  const updateTypewriter = (patch: Partial<ITypewriterTransitionSettings>) => {
    updateTransition({
      ...transition,
      typewriter: { ...transition.typewriter, ...patch },
    });
  };

  const onChangeTransitionType = (type: TransitionType) => {
    updateTransition(withTransitionType(transition, type, GetArrayOfKeys));
  };

  const onChangeTypewriterNumber =
    (
      field:
        | "fontSize"
        | "transitionSpeedStart"
        | "transitionSpeedStop"
        | "typingSpeed"
        | "showButtonDelay"
    ) =>
    (raw: string) => {
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) return;

      updateTypewriter({ [field]: parsed });
    };

  const isTypewriter = transition.transitionType === "typewriter";

  const gridClassName = classNames("viewport3d-transition-editor-grid", {
    "viewport3d-transition-editor-grid--disabled": disabled,
    "viewport3d-transition-editor-grid--typewriter": isTypewriter,
  });

  const defaultEditor = (
    <>
      <ActorPresetEditorField
        name="Label"
        icon={LabelOutlinedIcon}
        className="viewport3d-transition-editor-label"
      >
        <div className="viewport3d-transition-editor-stack">
          <TransitionRichTextField
            ariaLabel="transition-text-editor"
            label="Text"
            placeholder="Transition"
            locKey={transition.text}
            multiline
            className="viewport3d-transition-richtext--grow"
          />
          <PiAssetSelect
            label="Icon"
            assetId={transition.assetId}
            onChange={(assetId: string) =>
              updateTransition({
                ...transition,
                assetId: assetId,
              })
            }
          />
        </div>
      </ActorPresetEditorField>
      <ActorPresetEditorField
        name="Style & Timing"
        icon={PaletteOutlinedIcon}
        className="viewport3d-transition-editor-timing"
      >
        <div className="viewport3d-transition-editor-stack">
          <PiColorPickerField
            label="Color"
            color={transition.color}
            onChange={(color: RgbaColor) =>
              updateTransition({
                ...transition,
                color: color,
              })
            }
          />
          <PiInputfield
            label="Duration"
            placeholder="Duration"
            value={transition.duration.toString()}
            onChange={onChangeTransitionDuration}
          />
        </div>
      </ActorPresetEditorField>
    </>
  );

  const typewriter = transition.typewriter;

  const typewriterEditor = (
    <>
      <ActorPresetEditorField
        name="Text"
        icon={LabelOutlinedIcon}
        className="viewport3d-transition-editor-tw-text"
      >
        <div className="viewport3d-transition-editor-stack">
          <TransitionRichTextField
            ariaLabel="typewriter-title-editor"
            label="Title"
            placeholder="Title"
            locKey={typewriter.title}
          />
          <TransitionRichTextField
            ariaLabel="typewriter-text-editor"
            label="Text"
            placeholder="Text"
            locKey={typewriter.text}
            multiline
            className="viewport3d-transition-richtext--grow"
          />
          <div className="viewport3d-transition-editor-row">
            <PiInputfield
              label="Font size"
              placeholder="Font size"
              value={typewriter.fontSize.toString()}
              onChange={onChangeTypewriterNumber("fontSize")}
            />
            <PiSwitch
              label="Instant text"
              checked={typewriter.instantText}
              onChange={(instantText: boolean) =>
                updateTypewriter({ instantText })
              }
            />
          </div>
        </div>
      </ActorPresetEditorField>
      <div className="viewport3d-transition-editor-tw-settings">
        <ActorPresetEditorField name="Colors" icon={PaletteOutlinedIcon}>
          <div className="viewport3d-transition-editor-stack">
            <div className="viewport3d-transition-editor-row">
              <PiSwitch
                label="Use background color"
                checked={typewriter.useBackgroundColor}
                onChange={(useBackgroundColor: boolean) =>
                  updateTypewriter({ useBackgroundColor })
                }
              />
              <PiColorPickerField
                label="Background color"
                color={typewriter.backgroundColor}
                onChange={(backgroundColor: RgbaColor) =>
                  updateTypewriter({ backgroundColor })
                }
              />
            </div>
            <div className="viewport3d-transition-editor-row">
              <PiSwitch
                label="Use text color"
                checked={typewriter.useTextColor}
                onChange={(useTextColor: boolean) =>
                  updateTypewriter({ useTextColor })
                }
              />
              <PiColorPickerField
                label="Text color"
                color={typewriter.textColor}
                onChange={(textColor: RgbaColor) =>
                  updateTypewriter({ textColor })
                }
              />
            </div>
          </div>
        </ActorPresetEditorField>
        <ActorPresetEditorField name="Icons" icon={ImageOutlinedIcon}>
          <div className="viewport3d-transition-editor-stack">
            <div className="viewport3d-transition-editor-row">
              <PiAssetSelect
                label="Title icon"
                assetId={typewriter.titleIconAssetId}
                onChange={(titleIconAssetId: string) =>
                  updateTypewriter({ titleIconAssetId })
                }
              />
              <PiAssetSelect
                label="Bottom icon"
                assetId={typewriter.bottomIconAssetId}
                onChange={(bottomIconAssetId: string) =>
                  updateTypewriter({ bottomIconAssetId })
                }
              />
            </div>
          </div>
        </ActorPresetEditorField>
        <ActorPresetEditorField name="Timing" icon={TimerOutlinedIcon}>
          <div className="viewport3d-transition-editor-stack">
            <div className="viewport3d-transition-editor-row">
              <PiInputfield
                label="Transition speed start"
                placeholder="Transition speed start"
                value={typewriter.transitionSpeedStart.toString()}
                onChange={onChangeTypewriterNumber("transitionSpeedStart")}
              />
              <PiInputfield
                label="Transition speed stop"
                placeholder="Transition speed stop"
                value={typewriter.transitionSpeedStop.toString()}
                onChange={onChangeTypewriterNumber("transitionSpeedStop")}
              />
            </div>
            <div className="viewport3d-transition-editor-row">
              <PiInputfield
                label="Typing speed"
                placeholder="Typing speed"
                value={typewriter.typingSpeed.toString()}
                onChange={onChangeTypewriterNumber("typingSpeed")}
              />
              <PiInputfield
                label="Show button delay"
                placeholder="Show button delay"
                value={typewriter.showButtonDelay.toString()}
                onChange={onChangeTypewriterNumber("showButtonDelay")}
              />
            </div>
          </div>
        </ActorPresetEditorField>
      </div>
    </>
  );

  return (
    <div className="viewport3d-transition-editor">
      <div className="viewport3d-transition-editor-type-toggle">
        <PiSegmentedToggle<TransitionType>
          ariaLabel="Transition type"
          layoutId="viewport3d-transition-type"
          fullWidth
          disabled={disabled}
          value={transition.transitionType}
          onChange={onChangeTransitionType}
          options={[
            {
              label: "Default",
              value: "default",
              testId: "transition-type-default",
            },
            {
              label: "Typewriter",
              value: "typewriter",
              testId: "transition-type-typewriter",
            },
          ]}
        />
      </div>
      <div className={gridClassName} aria-disabled={disabled}>
        {isTypewriter ? typewriterEditor : defaultEditor}
      </div>
    </div>
  );
}
